import React from "react";
import { Box, Button, Checkbox, Chip, FormControlLabel, Pagination, Paper, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { DataTable, FormDialog, SelectField, SelectorBar } from "../../Layout/Common";
import { FIXED_TABLE, TIERS, tierOf, usd4 } from "./loc";

const mono = { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 };

// Coverage: tag by language coloured by tier, filtered by folder, languages, tiers, outdated and suggestions, with
// the totals per language and the two AI actions on the current scope (the English review, the context translation).
export default class CoverageTab extends React.Component {
    state = {
        folder: this.props.initialFolder || "*", langs: [], tiers: [], outdated: false, review: false,
        rows: [], total: 0, totals: {}, page: 1, limit: 100, loading: false,
        dialog: null,   // { kind: review|context, estimate, error, loading, force, all }
    };

    componentDidMount() {
        const { project } = this.props;
        this.setState({ langs: project.languages.map(l => l.code).filter(c => c !== project.baseLanguage) }, this.reload);
    }

    reload = () => {
        const { folder, langs, tiers, outdated, review, page, limit } = this.state;
        this.setState({ loading: true });
        const payload = { folders: folder && folder !== "*" ? [folder] : [], languages: langs, tiers, outdated, review, skip: (page - 1) * limit, limit };
        return this.props.loc("Loading coverage", ["coverage"], { payload, silent: true })
            .then(r => { if (r && typeof r !== "string") this.setState({ rows: r.rows ?? [], total: r.total ?? 0, totals: r.totals ?? {} }); })
            .catch(() => {})
            .finally(() => this.setState({ loading: false }));
    }

    set = (patch) => this.setState({ ...patch, page: 1 }, this.reload);
    changeFolder = (folder) => { this.props.onFolderChange(folder === "*" ? "" : folder); this.set({ folder }); }

    // ---------- the AI actions: a dry run first, the estimate in a dialog, then the run ----------
    openAction = (kind, extra = {}) => {
        const dialog = { kind, loading: true, force: false, all: false, token: ++this.dryRunToken, ...extra };
        this.setState({ dialog }, () => this.dryRun());
    }
    dryRunToken = 0;

    request(dryRun) {
        const { dialog, folder, langs } = this.state;
        const parts = dialog.kind === "review" ? ["review", "run"] : ["translate", "context"];
        if (folder && folder !== "*") parts.push(folder);
        const payload = dialog.kind === "review" ? { dryRun } : { languages: langs, force: !!dialog.force, all: !!dialog.all, dryRun };
        return this.props.loc(dryRun ? "Estimating" : "Starting the job", parts, { payload, silent: true, retry: false, timeout: 90000 });
    }

    dryRun = () => {
        const token = this.state.dialog?.token;
        const current = () => this.state.dialog && this.state.dialog.token === token;   // a slow estimate never lands on another dialog
        this.request(true).then(r => {
            if (!current()) return;
            if (typeof r === "string") { this.setState(s => ({ dialog: { ...s.dialog, loading: false, error: r } })); return; }
            this.setState(s => ({ dialog: { ...s.dialog, loading: false, estimate: r.estimate, nothing: !!r.nothing, message: r.message, model: r.model } }));
        }).catch(e => { if (current()) this.setState(s => ({ dialog: { ...s.dialog, loading: false, error: e.message } })); });
    }

    run = () => {
        this.setState(s => ({ dialog: { ...s.dialog, loading: true } }));
        this.request(false).then(r => {
            if (typeof r === "string") { this.setState(s => ({ dialog: { ...s.dialog, loading: false, error: r } })); return; }
            enqueueSnackbar(r.status === "done" ? r.summary : `Job ${r.status}: ${r.unitsTotal} unit(s)`, { variant: "success" });
            this.setState({ dialog: null });
            this.props.onJobsChanged();
            if (r.status === "done") this.reload();
        }).catch(e => this.setState(s => ({ dialog: { ...s.dialog, loading: false, error: e.message } })));
    }

    renderDialog() {
        const { dialog, folder, langs } = this.state;
        if (!dialog) return null;
        const est = dialog.estimate;
        const isReview = dialog.kind === "review";
        const set = (patch) => this.setState(s => ({ dialog: { ...s.dialog, ...patch, loading: true, error: null, estimate: null, token: ++this.dryRunToken } }), this.dryRun);
        return (
            <FormDialog open title={isReview ? "Review the English" : "Context translation"} onClose={() => this.setState({ dialog: null })} onConfirm={this.run}
                        confirmText="Run" confirmDisabled={dialog.loading || !!dialog.error || dialog.nothing} fullWidth maxWidth="sm">
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Scope: <b style={mono}>{folder && folder !== "*" ? folder : "every folder"}</b>{isReview ? "" : ` · ${langs.length} language${langs.length === 1 ? "" : "s"}`}.
                    {isReview ? " The AI reads every base text with its folder context and proposes corrections; nothing changes until you accept them on the Strings tab."
                              : " Every cell that is missing, outdated or at AI isolated is re-translated with the folder's texts, the project context and the glossary, and lands at AI context."}
                </Typography>
                {!isReview && <Box>
                    <FormControlLabel control={<Checkbox checked={dialog.force} onChange={e => set({ force: e.target.checked })} />} label="Also re-do AI context cells" />
                    <FormControlLabel control={<Checkbox checked={dialog.all} onChange={e => set({ all: e.target.checked })} />} label={<span>Every cell, human tiers too <b>(overwrites human work)</b></span>} />
                </Box>}
                {dialog.loading && <Typography color="text.secondary">Estimating…</Typography>}
                {dialog.error && <Typography color="error">{dialog.error}</Typography>}
                {dialog.nothing && <Typography color="text.secondary">{dialog.message}</Typography>}
                {est && !dialog.loading && !dialog.nothing && (
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
                        <Typography variant="body2">{est.requests} request{est.requests === 1 ? "" : "s"} · {est.cells} cell{est.cells === 1 ? "" : "s"} · {dialog.model}</Typography>
                        <Typography variant="h6">{usd4(est.usd)} <Typography component="span" variant="body2" color="text.secondary">estimated (up to {usd4(est.usdHigh)})</Typography></Typography>
                        <Typography variant="caption" color="text.secondary">{Math.round(est.inputTokens / 1000)}K input · {Math.round(est.outputTokens / 1000)}K output tokens. Small jobs finish in seconds; larger ones run on the scheduler, a few units per minute — the jobs panel follows them.</Typography>
                    </Paper>
                )}
            </FormDialog>
        );
    }

    render() {
        const { project, tree, canEdit } = this.props;
        const { folder, langs, tiers, outdated, review, rows, total, totals, page, limit, loading } = this.state;
        const codes = project.languages.map(l => l.code).filter(c => c !== project.baseLanguage);
        const shown = [project.baseLanguage, ...codes.filter(c => langs.includes(c))];
        const folderOptions = [{ value: "*", label: "(every folder)" }, ...tree.folders.slice().sort((a, b) => a.order - b.order).map(f => ({ value: f.path, label: `${f.path} (${f.tags})` }))];
        const totalCols = [
            { id: "lang", label: "Language", width: "20%", render: l => <b>{l}</b> },
            ...TIERS.map(t => ({ id: "t" + t.value, label: t.short, align: "right", width: "8%", sx: { bgcolor: t.color + "66" }, render: l => totals[l]?.tiers?.[t.value] ?? 0 })),
            { id: "outdated", label: "outdated", align: "right", width: "16%", render: l => totals[l]?.outdated ?? 0 },
            { id: "empty", label: "empty", align: "right", width: "16%", render: l => totals[l]?.empty ?? 0 },
        ];
        const gridCols = [
            { id: "path", label: "Tag", width: 340, render: r => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span style={mono}>{r.path}</span>
                    {r.review && <Chip size="small" color="secondary" label={r.review.kind === "fix" ? "fix" : "suggestion"} sx={{ height: 18, fontSize: 10 }} />}
                </Box>
            )},
            ...shown.map(lang => ({ id: lang, label: lang, align: "center", width: 52, render: r => {
                const c = r.cells?.[lang] ?? { tier: 0, outdated: false, empty: true };
                const t = tierOf(c.tier);
                return <Tooltip title={`${lang}: ${t.label}${c.outdated ? ", outdated" : ""}${c.empty ? ", empty" : ""}`}>
                    <Box sx={{ bgcolor: c.empty ? "#fafafa" : t.color, border: "1px solid", borderColor: c.outdated ? "warning.main" : "divider", borderRadius: 0.5, px: 0.5, fontSize: 10, textAlign: "center" }}>{c.empty ? "∅" : t.short}{c.outdated ? "!" : ""}</Box>
                </Tooltip>;
            }})),
        ];
        return (
            <Box>
                <SelectorBar sx={{ flexWrap: "wrap" }}>
                    <SelectField label="Folder" size="small" minWidth={280} value={folder} options={folderOptions} onChange={this.changeFolder} />
                    <ToggleButtonGroup size="small" value={tiers} onChange={(_, v) => this.set({ tiers: v })}>
                        {TIERS.map(t => <ToggleButton key={t.value} value={t.value} sx={{ textTransform: "none" }}><Tooltip title={t.label}><span>{t.short}</span></Tooltip></ToggleButton>)}
                    </ToggleButtonGroup>
                    <FormControlLabel control={<Checkbox size="small" checked={outdated} onChange={e => this.set({ outdated: e.target.checked })} />} label="outdated" />
                    <FormControlLabel control={<Checkbox size="small" checked={review} onChange={e => this.set({ review: e.target.checked })} />} label="with suggestions" />
                    <Box sx={{ flex: 1 }} />
                    {canEdit && <Button size="small" variant="outlined" onClick={() => this.openAction("review")}>Review English</Button>}
                    {canEdit && <Button size="small" variant="contained" onClick={() => this.openAction("context")}>Context translate</Button>}
                </SelectorBar>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1, alignItems: "center" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Languages:</Typography>
                    {codes.map(c => <Chip key={c} size="small" label={c} variant={langs.includes(c) ? "filled" : "outlined"} color={langs.includes(c) ? "primary" : "default"} onClick={() => this.set({ langs: langs.includes(c) ? langs.filter(l => l !== c) : [...langs, c] })} />)}
                    <Button size="small" onClick={() => this.set({ langs: codes })}>all</Button>
                    <Button size="small" onClick={() => this.set({ langs: [] })}>none</Button>
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Totals of the {total} matching tag{total === 1 ? "" : "s"}{loading ? " · loading…" : ""}</Typography>
                <DataTable columns={totalCols} rows={shown} getRowKey={l => l} sx={{ mb: 2, ...FIXED_TABLE }} />
                <DataTable columns={gridCols} rows={rows} getRowKey={r => r.path} sx={FIXED_TABLE} />
                {total > limit && <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}><Pagination count={Math.ceil(total / limit)} page={page} onChange={(_, p) => this.setState({ page: p }, this.reload)} size="small" /></Box>}
                {this.renderDialog()}
            </Box>
        );
    }
}
